import { createBrowserClient } from '@supabase/ssr';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_PUBLISHABLE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY || '';

let browserClient = null;

/**
 * Singleton browser Supabase client backed by cookies.
 *
 * The app's auth flows are server-orchestrated: login/signup/logout all
 * POST to `/api/auth/*` endpoints which mutate `sb-*` and `st-*` cookies
 * together. This client is intended for **read-only / monitoring** use:
 * `auth.getUser()` for the drift watcher, `auth.onAuthStateChange()` to
 * catch session expiry that happened outside our request flow.
 *
 * Do NOT call `signInWithPassword`, `signUp`, or `signOut` from the
 * browser: they bypass the Sharetribe side and leave the two sessions
 * out of sync.
 *
 * @returns {import('@supabase/supabase-js').SupabaseClient | null}
 */
export const getSupabase = () => {
  if (typeof window === 'undefined') return null;
  if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) return null;
  if (!browserClient) {
    browserClient = createBrowserClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY);
  }
  return browserClient;
};

/**
 * Mark the post-signup pricing-page onboarding interrupt as dismissed.
 * Writes to `raw_user_meta_data`; the auth lockdown trigger relocates the
 * key to `raw_app_meta_data` (where the BFF reads it on /api/membership).
 *
 * @returns {Promise<{ dismissedAt: string }>}
 * @throws  on auth failure or Supabase error.
 */
export const dismissMembershipOnboarding = async () => {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase client unavailable');
  const dismissedAt = new Date().toISOString();
  const { error } = await supabase.auth.updateUser({
    data: { membership_onboarding_dismissed_at: dismissedAt },
  });
  if (error) throw error;
  return { dismissedAt };
};
